import { cva, type VariantProps } from 'class-variance-authority'
import * as React from 'react'

import { cn } from '../lib/utils'

// Multi-line sibling of `TextField` — same bordered card surface and the same `state`
// color system (the border carries the tone at rest; a matching ring appears on focus,
// echoing the tone — WCAG 2.4.7). The ring uses color-mix as an arbitrary value because
// the es-* tokens are bare var() with no <alpha-value>, so `ring-red/opacity` would
// silently fall back to Tailwind's default blue. Differs from TextField only in being a
// single `<textarea>` (focus, not focus-within) that grows vertically and doesn't resize.
const textareaVariants = cva(
  'w-full min-h-[108px] resize-none rounded-xl border-[1.5px] bg-card px-3.5 py-3 text-base md:text-sm text-foreground shadow-card outline-none transition-colors placeholder:text-faint focus:ring-4 disabled:cursor-not-allowed disabled:opacity-50',
  {
    variants: {
      state: {
        default: 'border-border focus:border-brand-green-border focus:ring-brand-green-border/[0.14]',
        invalid: 'border-red focus:ring-[color-mix(in_srgb,oklch(var(--red))_14%,transparent)]',
        success: 'border-brand-green-border focus:ring-brand-green-border/20',
        warning: 'border-amber focus:ring-[color-mix(in_srgb,oklch(var(--amber))_14%,transparent)]'
      }
    },
    defaultVariants: { state: 'default' }
  }
)

export interface TextareaProps extends Omit<React.ComponentProps<'textarea'>, 'size'>, VariantProps<typeof textareaVariants> {
  /** Extra classes for the textarea (border, ring, background, min-height live here). */
  className?: string
}

/**
 * The app's standard multi-line input — the `<textarea>` counterpart to `TextField`, with
 * the same surface and `state` tones. Defaults to a comfortable min-height; override with
 * `className` (e.g. `min-h-[84px]`) or the native `rows`. Pass any native `<textarea>` prop.
 */
const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(({ className, state, disabled, ...props }, ref) => (
  <textarea
    ref={ref}
    disabled={disabled}
    className={cn(textareaVariants({ state }), className)}
    {...props}
    aria-invalid={props['aria-invalid'] ?? (state === 'invalid' || undefined)}
  />
))
Textarea.displayName = 'Textarea'

export { Textarea, textareaVariants }
